import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Space, Divider, Tag } from "antd";
import { MailOutlined, MobileOutlined, BellOutlined } from "@ant-design/icons";
import DOMPurify from "dompurify";
import { fetchSentNotification, getNotificationType } from "../../../api/communications/sentNotificationsApi";
import type { CommunicationFeedRow } from "../../../types/communications/CommunicationFeedTypes";
import type { SentNotification } from "../../../types/communications/NotificationTypes";
import { DIRECTION_LABEL, CHANNEL_LABELS, statusLabel } from "../../../types/communications/CommunicationTypes";

const { Title, Text } = Typography;

interface NotificationDetailModalProps {
	notification: SentNotification | CommunicationFeedRow | null;
	isOpen: boolean;
	onClose: () => void;
}

const isFeedRow = (notification: SentNotification | CommunicationFeedRow): notification is CommunicationFeedRow =>
	typeof (notification as CommunicationFeedRow).source === "string";

// Feed ids are `source:id` over three tables with overlapping keys; only `sent:` maps to a sent notification.
const sentNotificationId = (notification: SentNotification | CommunicationFeedRow): number | null => {
	if (!isFeedRow(notification)) return notification.id;
	return notification.source === "sent" ? Number(notification.id.split(":").pop()) : null;
};

const textBody = (value: string | null | undefined) => (
	<div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{value || "—"}</div>
);

const NotificationDetailModal: React.FC<NotificationDetailModalProps> = ({ notification, isOpen, onClose }) => {
	const [bodyError, setBodyError] = useState<boolean>(false);
	const [dispNotification, setDispNotification] = useState<SentNotification | null>(null);

	const sentId = notification ? sentNotificationId(notification) : null;
	const feedRow = notification && isFeedRow(notification) ? notification : null;

	useEffect(() => {
		setDispNotification(null);
		setBodyError(false);
		if (!isOpen || sentId === null) return;

		let cancelled = false;
		fetchSentNotification(sentId)
			.then((detail) => {
				if (!cancelled) setDispNotification(detail);
			})
			.catch(() => {
				if (!cancelled) setBodyError(true);
			});

		return () => {
			cancelled = true;
		};
	}, [isOpen, sentId]);

	const renderFeedRow = (row: CommunicationFeedRow) => (
		<Space direction="vertical" style={{ width: "100%" }} size="large">
			<div>
				<Title level={4}>{row.subject || row.type_label || "Communication"}</Title>
				<Divider style={{ margin: "12px 0" }} />
			</div>

			<div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
				<div><Text strong>Direction: </Text><Text>{DIRECTION_LABEL[row.direction] ?? row.direction}</Text></div>
				<div><Text strong>Channel: </Text><Text>{row.channel ? CHANNEL_LABELS[row.channel] ?? row.channel : "—"}</Text></div>
				<div><Text strong>Status: </Text><Text>{statusLabel(row.status)}</Text></div>
				<div><Text strong>Patient: </Text><Text>{row.patient_name ?? "—"}</Text></div>
				<div><Text strong>Contact: </Text><Text>{row.contact ?? "—"}</Text></div>
				{row.occurred_at && <div><Text strong>Date: </Text><Text>{new Date(row.occurred_at).toLocaleString()}</Text></div>}
			</div>

			<div data-testid="notification-detail-body">{textBody(row.preview)}</div>
		</Space>
	);

	const renderSentNotification = (sent: SentNotification) => (
		<Space direction="vertical" style={{ width: "100%" }} size="large">
			<div>
				<Title level={4}>
					{sent.subject || "No Subject"}
				</Title>
				<Divider style={{ margin: "12px 0" }} />
			</div>

			<div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
				<div>
					<Text strong>Template: </Text>
					<Text>{sent.template_name}</Text>
				</div>

				<div>
					<Text strong>Channel: </Text>
					{(() => {
						const type = getNotificationType(sent);
						switch (type) {
							case 'email': return <Tag icon={<MailOutlined />} color="blue">Email</Tag>;
							case 'sms': return <Tag icon={<MobileOutlined />} color="green">SMS</Tag>;
							case 'push': return <Tag icon={<BellOutlined />} color="purple">Push</Tag>;
							case 'both': return <><Tag icon={<MailOutlined />} color="blue">Email</Tag><Tag icon={<MobileOutlined />} color="green">SMS</Tag></>;
							default: return <Tag>Unknown</Tag>;
						}
					})()}
				</div>

				<div>
					<Text strong>Sent At: </Text>
					<Text>{new Date(sent.created_at).toLocaleString()}</Text>
				</div>

				{sent.recipient_user_id && (
					<div>
						<Text strong>User ID: </Text>
						<Text>{sent.recipient_user_id}</Text>
					</div>
				)}
			</div>

			<div data-testid="notification-detail-body">
				{sent.parsed_template
					? <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(sent.parsed_template) }} />
					: textBody(sent.preview)}
			</div>
		</Space>
	);

	const renderContent = () => {
		if (!notification) return <Text type="secondary">No notification selected</Text>;
		if (sentId === null && feedRow) return renderFeedRow(feedRow);
		if (dispNotification) return renderSentNotification(dispNotification);
		if (bodyError) return <Text type="secondary">{notification.preview || "Unable to load this message."}</Text>;
		return <Text type="secondary">Loading message…</Text>;
	};

	return (
		<Modal
			open={isOpen}
			onCancel={onClose}
			destroyOnClose
			footer={[
				<Button key="close" type="primary" onClick={onClose}>
					Close
				</Button>,
			]}
			width={800}
			bodyStyle={{ maxHeight: "70vh", overflow: "auto" }}
		>
			<div data-testid="notification-detail-modal">{renderContent()}</div>
		</Modal>
	);
};

export default NotificationDetailModal;
